/**
 * useProductDefinitions — Shared hook for querying product definitions (DRR-095/096/101).
 *
 * Products are Layer 2 config sets that bundle workflows, rulesets, settings,
 * overrides, constellations, and lenses. This hook eliminates the duplicated
 * inline gql query between ProductSelectionStep and ProductCatalogPage.
 *
 * Dev fallback: Mock products when backend unavailable.
 *
 * @example
 * ```tsx
 * const { products, loading, error } = useProductDefinitions({ industry: 'assisted_living' });
 * ```
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import type { GqlProductDefinition } from '@/generated/graphql';

export const LIST_PRODUCT_DEFINITIONS = gql`
  query ListProductDefinitions($industry: String, $jurisdiction: String) {
    productDefinitions(industry: $industry, jurisdiction: $jurisdiction) {
      productId
      name
      description
      industry
      jurisdiction
      version
      status
      brand
      subgraphs
      requiredRulesets
      activatesWorkflows
      activatesSettings
      activatesOverrides
      activatesConstellations
      onboardingLenses
      provisioningStatus
    }
  }
`;

export interface UseProductDefinitionsOptions {
  industry?: string | null;
  jurisdiction?: string | null;
}

export interface UseProductDefinitionsReturn {
  products: GqlProductDefinition[];
  loading: boolean;
  error: Error | undefined;
}

// Dev-mode env detection (same pattern as useTenantSubgraphConfig)
const getEnvConfig = (): Record<string, string> => {
  if (typeof window !== 'undefined' && (window as unknown as { __ENV__?: Record<string, string> }).__ENV__) {
    return (window as unknown as { __ENV__: Record<string, string> }).__ENV__;
  }
  return {};
};
const USE_MOCK_AUTH = getEnvConfig().ENABLE_MOCK_AUTH === 'true';
const USE_DEV_MODE = getEnvConfig().ENABLE_DEV_MODE === 'true';

/**
 * Mock product definitions for dev-mode fallback.
 * Derived from common/configs/seed/products configs.
 */
const MOCK_PRODUCTS: GqlProductDefinition[] = [
  {
    __typename: 'GqlProductDefinition',
    productId: 'alf-mn-medicaid',
    name: 'MN Assisted Living (Medicaid)',
    description: 'Full-stack ALF with Medicaid Elderly Waiver support',
    industry: 'assisted_living',
    jurisdiction: 'MN',
    version: '1.0',
    status: 'active',
    brand: null,
    subgraphs: ['ferp-core', 'fhir', 'erp', 'edi', 'payroll', 'scheduling'],
    requiredRulesets: ['rs-5-0-001', 'rs-5-1-001'],
    activatesWorkflows: ['wf-0-0-001', 'wf-2-0-001'],
    activatesSettings: ['st-0-0'],
    activatesOverrides: [],
    activatesConstellations: ['constellation-care-service-plan', 'constellation-staff-training', 'constellation-payroll-workforce'],
    onboardingLenses: ['ln-1-0-001'],
    provisioningStatus: null,
  },
  {
    __typename: 'GqlProductDefinition',
    productId: 'hc-home-care',
    name: 'Home Care Agency',
    description: 'Home health and home care with Medicare PDGM billing',
    industry: 'home_care',
    jurisdiction: null,
    version: '1.0',
    status: 'active',
    brand: null,
    subgraphs: ['ferp-core', 'fhir', 'erp', 'edi', 'payroll', 'scheduling', 'time-tracking'],
    requiredRulesets: ['rs-5-0-001'],
    activatesWorkflows: ['wf-0-0-001'],
    activatesSettings: [],
    activatesOverrides: [],
    activatesConstellations: ['constellation-care-service-plan', 'constellation-staff-training', 'constellation-payroll-workforce'],
    onboardingLenses: [],
    provisioningStatus: null,
  },
  {
    __typename: 'GqlProductDefinition',
    productId: 'generic-business',
    name: 'General Business',
    description: 'Core business operations: accounting, payroll, and workflow management',
    industry: 'accounting',
    jurisdiction: null,
    version: '1.0',
    status: 'active',
    brand: null,
    subgraphs: ['ferp-core', 'erp', 'payroll'],
    requiredRulesets: [],
    activatesWorkflows: [],
    activatesSettings: [],
    activatesOverrides: [],
    activatesConstellations: ['constellation-payroll-workforce'],
    onboardingLenses: [],
    provisioningStatus: null,
  },
] as unknown as GqlProductDefinition[];

export function useProductDefinitions(
  options: UseProductDefinitionsOptions = {}
): UseProductDefinitionsReturn {
  // Skip the query entirely in dev/mock mode (same pattern as useTenantSubgraphConfig)
  const skipQuery = USE_MOCK_AUTH || USE_DEV_MODE;

  const { data, loading, error } = useQuery<{
    productDefinitions: GqlProductDefinition[];
  }>(LIST_PRODUCT_DEFINITIONS, {
    variables: {
      industry: options.industry ?? undefined,
      jurisdiction: options.jurisdiction ?? undefined,
    },
    fetchPolicy: 'cache-and-network',
    skip: skipQuery,
  });

  const backendData = data?.productDefinitions;
  const backendFailed = error != null || (!loading && !backendData);

  const products = useMemo(() => {
    if (backendData && backendData.length > 0) {
      return backendData;
    }

    if ((USE_DEV_MODE || USE_MOCK_AUTH) && backendFailed) {
      console.warn('⚠️  Using MOCK product definitions (backend unavailable, dev mode fallback)');
      let filtered = MOCK_PRODUCTS;
      if (options.industry) {
        filtered = filtered.filter((p) => p.industry === options.industry);
      }
      if (options.jurisdiction) {
        filtered = filtered.filter((p) => p.jurisdiction === options.jurisdiction || !p.jurisdiction);
      }
      return filtered;
    }

    return [];
  }, [backendData, backendFailed, options.industry, options.jurisdiction]);

  return {
    products,
    loading: loading && !skipQuery,
    error: backendFailed && !skipQuery ? error : undefined,
  };
}

// Re-export the generated type for consumers that need it
export type { GqlProductDefinition as ProductDefinition } from '@/generated/graphql';
